import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const IncidentTimeline = ({ events, onAddEvent, onRemoveEvent, readOnly = false }) => {
  const [showForm, setShowForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ time: '', description: '', actor: '' });

  const handleAdd = () => {
    if (!newEvent?.time || !newEvent?.description) return;
    onAddEvent?.({ ...newEvent, id: Date.now() });
    setNewEvent({ time: '', description: '', actor: '' });
    setShowForm(false);
  };

  const typeIcons = {
    incident: { icon: 'AlertTriangle', color: 'text-red-500 bg-red-100 dark:bg-red-900/30' },
    action: { icon: 'Zap', color: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30' },
    discovery: { icon: 'Eye', color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' },
    response: { icon: 'Shield', color: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30' },
    default: { icon: 'Clock', color: 'text-muted-foreground bg-muted' },
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Icon name="Clock" size={18} className="text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Incident Timeline</h3>
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          >
            <Icon name="Plus" size={13} />
            Add Event
          </button>
        )}
      </div>

      {showForm && !readOnly && (
        <div className="mb-5 p-4 bg-muted/40 border border-border rounded-lg space-y-3">
          <h4 className="text-sm font-medium text-foreground">New Timeline Event</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Date & Time *</label>
              <input
                type="datetime-local"
                value={newEvent?.time}
                onChange={e => setNewEvent(p => ({ ...p, time: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Person / Actor</label>
              <input
                type="text"
                placeholder="Who was involved?"
                value={newEvent?.actor}
                onChange={e => setNewEvent(p => ({ ...p, actor: e?.target?.value }))}
                className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Description *</label>
            <textarea
              placeholder="Describe what happened at this point..."
              value={newEvent?.description}
              onChange={e => setNewEvent(p => ({ ...p, description: e?.target?.value }))}
              rows={2}
              className="w-full text-sm px-3 py-2 bg-background border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              Add Event
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-3 py-1.5 bg-muted text-muted-foreground text-xs font-medium rounded-lg hover:bg-muted/80 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {events?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Clock" size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No timeline events recorded yet</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
          <div className="space-y-4">
            {events?.map((event, idx) => {
              const typeKey = event?.type || 'default';
              const { icon, color } = typeIcons?.[typeKey] || typeIcons?.default;
              return (
                <div key={event?.id || idx} className="relative flex gap-4 pl-12">
                  <div className={`absolute left-2.5 w-5 h-5 rounded-full flex items-center justify-center ${color} flex-shrink-0`}>
                    <Icon name={icon} size={11} />
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-foreground leading-snug">{event?.description}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {event?.time && (
                            <span className="flex items-center gap-1">
                              <Icon name="Clock" size={11} />
                              {format(new Date(event.time), 'MMM dd, yyyy HH:mm')}
                            </span>
                          )}
                          {event?.actor && (
                            <span className="flex items-center gap-1">
                              <Icon name="User" size={11} />
                              {event?.actor}
                            </span>
                          )}
                        </div>
                      </div>
                      {!readOnly && (
                        <button
                          onClick={() => onRemoveEvent?.(event?.id)}
                          className="p-1 rounded hover:bg-red-100 hover:text-red-600 text-muted-foreground transition-colors flex-shrink-0"
                        >
                          <Icon name="Trash2" size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentTimeline;
